import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Moon, Plus, Trash2, Loader2, CalendarClock } from "lucide-react";
import { fetchLunations, fetchDeployments, addDeployment, deleteDeployment } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];
const days = (d) => (Math.round(d) <= 0 ? "now" : `in ${Math.round(d)}d`);

const WindowRow = ({ ev, deployments, onAdd, onRemove }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", amount: "", note: "" });
  const [busy, setBusy] = useState(false);
  const label = ev.type === "new" ? "New Moon" : "Full Moon";
  const mine = deployments.filter(
    (d) => d.event_date === ev.date.slice(0, 10) && d.event_type === ev.type
  );

  const submit = async () => {
    if (form.title.trim().length < 2) return toast.error("Name the deployment.");
    setBusy(true);
    try {
      await onAdd({
        event_type: ev.type,
        event_date: ev.date.slice(0, 10),
        sign: ev.sign,
        title: form.title.trim(),
        amount: form.amount.trim(),
        note: form.note.trim(),
      });
      setForm({ title: "", amount: "", note: "" });
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#C5A059]/15 bg-black/30 p-5" data-testid={`planner-window-${ev.type}-${ev.date.slice(0,10)}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Moon size={16} className="text-[#C5A059]" />
          <div>
            <p className="text-sm font-light text-white/85">
              {label} · {ev.glyph} {ev.sign}
            </p>
            <p className="text-[0.6rem] uppercase tracking-[0.14em] text-white/40">
              {ev.date.slice(0, 10)} · {days(ev.days_until)}
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 rounded-full border border-[#C5A059]/40 px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.16em] text-[#C5A059] transition-colors hover:bg-[#C5A059] hover:text-[#050505]"
          data-testid={`planner-add-toggle-${ev.type}`}
        >
          <Plus size={12} /> Schedule
        </button>
      </div>

      {mine.length > 0 && (
        <div className="mt-4 space-y-2">
          {mine.map((d) => (
            <div key={d.id} className="group flex items-center justify-between rounded-xl border border-[#C5A059]/12 bg-[#070707] px-4 py-2.5">
              <div>
                <p className="text-sm font-light text-white/75">
                  {d.title} {d.amount && <span className="text-[#C5A059]">· {d.amount}</span>}
                </p>
                {d.note && <p className="text-[0.7rem] font-light text-white/40">{d.note}</p>}
              </div>
              <button
                onClick={() => onRemove(d.id)}
                className="text-white/20 opacity-0 transition-opacity hover:text-[#a35] group-hover:opacity-100"
                data-testid={`planner-remove-${d.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease }}
            className="overflow-hidden"
          >
            <div className="mt-4 space-y-2">
              <div className="flex gap-2">
                <input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Deployment (e.g. wire first tranche)"
                  className="flex-1 rounded-lg border border-[#C5A059]/25 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                  data-testid={`planner-title-${ev.type}`}
                />
                <input
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="Amount"
                  className="w-28 rounded-lg border border-[#C5A059]/25 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                  data-testid={`planner-amount-${ev.type}`}
                />
              </div>
              <div className="flex gap-2">
                <input
                  value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Note (optional)"
                  className="flex-1 rounded-lg border border-[#C5A059]/25 bg-black/40 px-3 py-2 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                />
                <button
                  onClick={submit}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#C5A059] bg-[#C5A059] px-4 py-2 text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[#050505] disabled:opacity-60"
                  data-testid={`planner-save-${ev.type}`}
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CyclicalPlanner = () => {
  const [lunations, setLunations] = useState([]);
  const [deployments, setDeployments] = useState([]);

  const load = async () => {
    try {
      const [l, d] = await Promise.all([fetchLunations(), fetchDeployments()]);
      setLunations(l.slice(0, 4));
      setDeployments(d);
    } catch (e) {}
  };
  useEffect(() => {
    load();
  }, []);

  const onAdd = async (payload) => {
    const dep = await addDeployment(payload);
    setDeployments((prev) => [...prev, dep]);
    toast.success("Deployment scheduled to the moon window.");
  };
  const onRemove = async (id) => {
    const prev = deployments;
    setDeployments((d) => d.filter((x) => x.id !== id));
    try {
      await deleteDeployment(id);
    } catch (e) {
      setDeployments(prev);
    }
  };

  return (
    <div className="mt-5" data-testid="cyclical-planner">
      <div className="mb-4 flex items-center gap-2 text-[#C5A059]">
        <CalendarClock size={14} />
        <p className="overline text-[0.5rem]">Schedule against upcoming moon windows</p>
      </div>
      <div className="space-y-3">
        {lunations.map((ev) => (
          <WindowRow
            key={`${ev.type}-${ev.date}`}
            ev={ev}
            deployments={deployments}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        ))}
      </div>
    </div>
  );
};

export default CyclicalPlanner;
